/**
 * Qué enseña la interfaz según el rol · `BFF_BO.md` §5.3
 *
 * > **Ocultar es cortesía; el servidor es quien decide.** Si una pantalla se salta
 * > la comprobación, la API responde 403 igual. Pero un menú lleno de opciones que
 * > no funcionan es una aplicación que parece rota.
 *
 * Esto es la cortesía, y está aquí —en funciones puras, con pruebas— porque los
 * permisos de §P.1 **no son un booleano**. La ficha médica y los contactos de
 * emergencia tienen reglas distintas y asimétricas, y deducirlas en cada
 * componente es como una pantalla acaba enseñando un botón que da `403`.
 *
 * ## Por qué hay reglas para `coach` si el panel es de `owner` y `admin`
 *
 * `surfacesOf` en el backend solo añade `panel` a `can_open` para `owner` y
 * `admin`: un entrenador abre la app del maestro, no esto. Parecería que las
 * ramas de `coach` no se alcanzan nunca.
 *
 * **Se alcanzan.** `can_open` se calcula sobre **todas** las membresías juntas y
 * el rol es **de cada club**: quien es administrativo en un club y entrenador en
 * otro entra al panel por el primero y, con el selector de club de §5.2, se
 * planta en el segundo siendo entrenador. Es el caso que §5.2 llama «no raro».
 *
 * Por eso se comprueba el rol **de la membresía elegida** y no `can_open`.
 *
 * Y de paso: estas mismas reglas son las que necesita §M —la ficha médica en el
 * tatami, con su propia ruta `GET /clubs/{club}/coach/students/{id}/health`—, así
 * que cuando se abra `apps/` esto es lo que hay que copiar.
 */

export type Rol = 'owner' | 'admin' | 'coach' | 'assistant';

const esRol = (valor: string): valor is Rol =>
  valor === 'owner' ||
  valor === 'admin' ||
  valor === 'coach' ||
  valor === 'assistant';

/** Un rol que no conocemos se trata como el más limitado, nunca como el más amplio. */
export const aRol = (valor: string | null | undefined): Rol =>
  valor && esRol(valor) ? valor : 'assistant';

const DUEÑO_O_ADMIN: readonly Rol[] = ['owner', 'admin'];
const CON_EL_TATAMI: readonly Rol[] = ['owner', 'admin', 'coach'];

/** Alumnos, tarifas, pagadores: escribir es de dueño y administrativo. */
export const puedeEditarAlumnos = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

export const puedeEditarPagadores = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * La ficha médica: **la ve y la escribe también el entrenador.**
 *
 * «Categoría especial de datos (RGPD art. 9). El entrenador la ve porque es quien
 * está delante cuando a un niño le da una reacción alérgica; el asistente no.»
 */
export const puedeVerFichaMedica = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

export const puedeEditarFichaMedica = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

/**
 * Los contactos de emergencia: **el entrenador los ve pero no los cambia.**
 *
 * La lectura es suya porque «es quien tiene el móvil en la mano en el peor
 * momento». La escritura no: el orden de la lista dice a quién se llama primero, y
 * eso lo decide la familia con el club, no quien está dando clase.
 *
 * No es una preferencia nuestra: `PUT /emergency-contacts` declara
 * `roles: ['owner', 'admin']` y responde `403` a un entrenador.
 */
export const puedeVerContactos = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

export const puedeEditarContactos = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * Generar el calendario es de dueño y administrativo: escribe en bloque el mes
 * entero de todos los grupos.
 */
export const puedeGenerarCalendario = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * Mover o cancelar **un día concreto** sí lo puede el entrenador.
 *
 * `BFF_BO.md` §7.5 lo dice y da la razón: «**el entrenador puede hacerlo**: es
 * quien está en la puerta cuando el polideportivo cierra un martes». No toca el
 * horario del grupo, solo ese día.
 */
export const puedeMoverUnaClase = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

/**
 * Quién da un grupo: **el entrenador no lo ve.**
 *
 * `BFF_BO.md` §7.5: «el entrenador ve los matriculados —es a quién da clase— y
 * **no ve los profesores del grupo**, que es organización del club». Y el propio
 * endpoint lo dice: `GET /coaches` declara solo `own` y `adm`, así que a un
 * entrenador le responde `403`.
 *
 * Es la tercera asimetría de §P.2, y la única donde el entrenador ve **menos** que
 * en el resto: puede cancelar una clase y no puede saber quién la da.
 */
export const puedeVerProfesores = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * La cuota de un alumno: **el entrenador no la ve.**
 *
 * `BFF_BO.md` §5.3 lo pone en la matriz con una cruz gorda: «tarifas y descuentos»
 * es **✗** para entrenador y asistente. Y es lo primero que pregunta un club con
 * empleados —«un entrenador nunca ve datos bancarios»—, así que la sección no se
 * enseña en vez de enseñar un botón que da `403`.
 */
export const puedeVerCuota = (rol: Rol): boolean => DUEÑO_O_ADMIN.includes(rol);

/**
 * Lo pendiente y las cifras de Inicio (§P.13): dueño y administrativo.
 *
 * `GET /clubs/{club}/today` responde `403` al entrenador. Y no le falta nada: casi
 * todo lo que cuenta —el contrato, la remesa, los recibos vencidos— no es suyo. El
 * que es administrativo en un club y entrenador en otro ve la campana en el primero
 * y no en el segundo.
 */
export const puedeVerLoPendiente = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

export const puedeEditarCuota = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

export const puedeEditarProfesores = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * Los grados: **el entrenador sí**, y esto es una asimetría a propósito.
 *
 * `POST /students/{id}/ranks` y `/stripes` aceptan `coa`, al revés que casi todo
 * §P.3 y §P.4. Tiene sentido: quien decide que alguien está listo para el amarillo
 * es quien le da clase, no quien lleva las cuentas. Lo que el entrenador no toca es
 * el dinero.
 *
 * El asistente no: `endpoints.md` §P.5 no lo incluye en ninguna de las dos.
 */
export const puedeVerGrados = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

export const puedeOtorgarGrados = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

/**
 * Cambiar la escalera de cinturones: **solo el dueño y el administrativo.**
 *
 * `PUT /belt-schemes/{id}/ranks` declara `own` y `adm`. Y la razón se lee sola en
 * lo que hace: sustituye la escalera entera y **borra los cinturones que falten**.
 * Eso no es «dar un grado», es cambiar el sistema del club.
 */
export const puedeEditarLaEscalera = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * El temario técnico: **el entrenador añade y corrige, pero no quita.**
 *
 * No es un olvido del contrato, es la asimetría que declara: `GET`, `POST` y `PATCH`
 * de `/curriculum` aceptan `coa`; el `DELETE` solo `own` y `adm`.
 *
 * Y se lee sola en lo que significa cada una. Escribir el temario es el trabajo del
 * que da clase: él sabe que al amarillo le falta una técnica y él la corrige cuando
 * ve que estaba mal escrita. Quitar del temario una técnica que llevaba tres años es
 * otra cosa: es decidir que el club deja de exigirla.
 */
export const puedeEditarElTemario = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

export const puedeQuitarDelTemario = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * Publicar un aviso: **el entrenador sí.**
 *
 * `POST /announcements` acepta `coa`, y es la misma lógica que mover una clase: es
 * quien está en la puerta cuando el polideportivo cierra un martes, y avisar de eso
 * no puede depender de que el dueño mire el móvil.
 */
export const puedeVerAvisos = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

export const puedePublicarAvisos = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

/**
 * Corregir y retirar, **solo el dueño y el administrativo**.
 *
 * Es la asimetría de §P.6 y el contrato la declara así: `PATCH` y `DELETE` son
 * `own` y `adm`. Publicar es contar algo; retirar lo que contó otro es otra cosa.
 */
export const puedeCorregirAvisos = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * **Leer la marca del club**: solo el dueño y el administrativo.
 *
 * `GET /clubs/{club}/branding` declara `own` y `adm`, y esto no está aquí para
 * esconder un botón: está para **no hacer la petición**.
 *
 * El marco del panel la pide en cada carga para teñir la interfaz, sin mirar el rol.
 * A quien no la puede leer le responde `403`, y el marco pintaba ese fallo como una
 * caja roja de «Sin permiso» **encima de todas las pantallas**: un error permanente
 * sobre algo que quien lo lee no puede arreglar y que no le hace falta.
 *
 * Se vio el 18-09-2026 abriendo una ruta del panel con una cuenta de entrenadora, y
 * eso destapó además que las rutas del panel no comprobaban `can_open` —solo la
 * puerta—; aquello se arregló aparte, en `SoloQuienAbreElPanel`.
 *
 * **Pero el `403` de la marca no era solo eso, y por eso este permiso hace falta.**
 * Es el caso de la cabecera de este fichero: quien es administrativo en un club y
 * entrenador en otro entra al panel por el primero —su `can_open` sí trae `panel`— y
 * con el selector de club se planta en el segundo siendo entrenador. Ahí la marca del
 * segundo club le responde `403` con todo el derecho, y el panel se le llenaba de
 * rojo. `BFF_BO.md` §5.2 llama a ese caso «no raro».
 *
 * Sin marca la interfaz sale con la paleta de la casa, que es exactamente lo que
 * `variablesDeMarca({})` produce.
 */
export const puedeVerLaMarca = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * Los ajustes del club: **el entrenador no entra.**
 *
 * `GET /clubs/{club}` sí acepta `coa` —necesita la zona horaria y el nombre—, pero
 * una sección llamada «El club» que solo se puede leer es una sección que invita a
 * tocar algo que va a dar `403`. Lo que el entrenador necesita de aquí ya le llega
 * por la cabecera del panel.
 */
export const puedeVerElClub = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

export const puedeEditarElClub = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * La competición: **los tres.**
 *
 * Y es lo contrario que en los informes, a propósito. Trece de las dieciséis rutas de
 * §P.10 declaran `own`, `adm` y `coa` porque **quien lleva a los niños al campeonato
 * el sábado es el entrenador**: es quien sabe quién está para competir, quien los pesa
 * y quien apunta los resultados en la grada.
 *
 * Las tres que no son suyas son las licencias federativas —papeles y dinero—, y eso
 * se resuelve escondiéndole **la pestaña**, no la sección entera.
 */
export const puedeVerCompeticion = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

export const puedeVerLicencias = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * El material: **dueño y administrativo.**
 *
 * Once de las doce rutas de §P.11 declaran `own` y `adm`. La doceava —leer el
 * catálogo— la puede también un entrenador, y aun así la sección **no se le enseña**:
 * de cuatro pestañas tres le darían `403`, y un menú lleno de opciones que no
 * funcionan es una aplicación que parece rota. Si algún día un entrenador necesita
 * consultar precios, el sitio es la ficha del alumno, no una sección de gestión.
 */
export const puedeVerMaterial = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

export const puedeEditarMaterial = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * Cerrar una sede: **solo el dueño.**
 *
 * `DELETE /venues/{id}` declara `own` a secas, y es coherente con lo que hace: una
 * sede cerrada deja sin sitio a sus grupos y el histórico de clases apunta a ella.
 * Editarla y añadirla sí las puede el administrativo.
 */
export const puedeCerrarUnaSede = (rol: Rol): boolean => rol === 'owner';

/**
 * El personal: **solo el dueño**, las cuatro rutas.
 *
 * Es la única parte del panel donde ni el administrativo entra, y la razón está en
 * lo que se decide ahí: quién ve el dinero del club y quién no. Dárselo a quien
 * lleva las cuentas sería dejar que se ampliara sus propios permisos.
 */
export const puedeGestionarPersonal = (rol: Rol): boolean => rol === 'owner';

/**
 * Cumplimiento: **dueño y administrativo**, como casi todo §P.8.
 *
 * Las tres rutas de fase 1 que llevan datos —consentimientos, registro de accesos y
 * sesiones de soporte— declaran `own` y `adm`. El entrenador no: los permisos de
 * imagen de ciento veinte familias no son cosa de quien da clase.
 */
export const puedeVerCumplimiento = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);

/**
 * Firmar el contrato de encargo: **solo el dueño**, y las dos rutas del DPA también.
 *
 * `GET` y `POST /dpa` declaran `own` a secas. Tiene sentido hasta en lo pequeño: es
 * un contrato entre dos empresas, y quien lo firma responde delante de la Agencia.
 * Un administrativo no puede obligar al club a nada.
 */
export const puedeFirmarElContrato = (rol: Rol): boolean => rol === 'owner';

/**
 * Informes: **el entrenador entra, pero solo a uno.**
 *
 * De los nueve informes de §P.9, `attendance` es el único que declara `coa`. Es la
 * misma lógica que en todo el panel: quién viene a su clase es suyo; el dinero, no.
 *
 * Por eso la sección la ve —si no, no llegaría a la asistencia— y el permiso de cada
 * informe se comprueba aparte. Un único booleano aquí obligaría a elegir entre
 * esconderle la asistencia o enseñarle una pestaña de ingresos que da `403`.
 */
export const puedeVerInformes = (rol: Rol): boolean =>
  CON_EL_TATAMI.includes(rol);

/** Los ocho informes de dinero y de gestión: `own` y `adm`. */
export const puedeVerInformesDeGestion = (rol: Rol): boolean =>
  DUEÑO_O_ADMIN.includes(rol);
